import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { RefreshCw, Play, Plane, Hotel, Ship, CheckCircle2, XCircle, Loader2, LucideIcon } from "lucide-react";
import { safeFormat } from "@/lib/formatDateTime";
import type { TripData } from "@/hooks/useTrips";

interface Trip {
  id: string;
  display_name: string;
  destination: string | null;
  start_date: string | null;
  trip_data: TripData | null;
}

interface EnrichmentResult {
  function: string;
  success: boolean;
  message: string;
  data?: Record<string, unknown>;
}

type EnrichmentFunction =
  | "enrich-flight"
  | "enrich-flight-price"
  | "enrich-hotel-price"
  | "enrich-cruise";

const ENRICHMENT_FUNCTIONS: { id: EnrichmentFunction; label: string; icon: LucideIcon; description: string }[] = [
  {
    id: "enrich-flight",
    label: "Flight Operational Data",
    icon: Plane,
    description: "Aviationstack: gates, terminals, aircraft, delays",
  },
  {
    id: "enrich-flight-price",
    label: "Flight Pricing",
    icon: Plane,
    description: "Duffel: retail price, duration",
  },
  {
    id: "enrich-hotel-price",
    label: "Hotel Pricing",
    icon: Hotel,
    description: "Xotelo: retail price, ratings",
  },
  {
    id: "enrich-cruise",
    label: "Cruise Itinerary",
    icon: Ship,
    description: "Apify Cruisemapper: port stops, dates",
  },
];

export function EnrichmentDebugPanel() {
  const queryClient = useQueryClient();
  const [selectedTripId, setSelectedTripId] = useState<string>("");
  const [selectedFunctions, setSelectedFunctions] = useState<Set<EnrichmentFunction>>(new Set());
  const [results, setResults] = useState<EnrichmentResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  // Fetch all trips for the dropdown
  const { data: trips, isLoading: tripsLoading } = useQuery({
    queryKey: ["adminAllTrips"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trips")
        .select("id, display_name, destination, start_date, trip_data")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Trip[];
    },
  });

  const selectedTrip = trips?.find((t) => t.id === selectedTripId);

  const toggleFunction = (fn: EnrichmentFunction) => {
    setSelectedFunctions((prev) => {
      const next = new Set(prev);
      if (next.has(fn)) {
        next.delete(fn);
      } else {
        next.add(fn);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedFunctions(new Set(ENRICHMENT_FUNCTIONS.map((f) => f.id)));
  };

  const selectNone = () => {
    setSelectedFunctions(new Set());
  };

  const runEnrichment = async () => {
    if (!selectedTrip || selectedFunctions.size === 0) return;

    setIsRunning(true);
    setResults([]);
    const newResults: EnrichmentResult[] = [];

    const tripData = selectedTrip.trip_data || {};
    const flights = tripData.flights || [];
    const hotels = tripData.hotels || [];
    const cruises = tripData.cruises || [];

    try {
      // Run flight operational enrichment
      if (selectedFunctions.has("enrich-flight")) {
        for (let i = 0; i < flights.length; i++) {
          const flight = flights[i];
          try {
            const { data, error } = await supabase.functions.invoke("enrich-flight", {
              body: {
                flight_number: flight.flight_number,
                flight_date: flight.departure_time || flight.departure_date,
              },
            });

            newResults.push({
              function: `enrich-flight (${flight.flight_number})`,
              success: !error && data?.enrichment,
              message: error?.message || (data?.enrichment ? "Enrichment data found" : "No data returned"),
              data: data?.enrichment,
            });
          } catch (err) {
            newResults.push({
              function: `enrich-flight (${flight.flight_number})`,
              success: false,
              message: err instanceof Error ? err.message : "Exception occurred",
            });
          }
        }
        if (flights.length === 0) {
          newResults.push({
            function: "enrich-flight",
            success: false,
            message: "No flights in this trip",
          });
        }
      }

      // Run flight price enrichment
      if (selectedFunctions.has("enrich-flight-price")) {
        if (flights.length > 0) {
          try {
            const flightInputs = flights.map((f) => ({
              departure_airport: f.departure_airport,
              arrival_airport: f.arrival_airport,
              departure_date: f.departure_date || f.departure_time,
              flight_number: f.flight_number,
            }));

            const { data, error } = await supabase.functions.invoke("enrich-flight-price", {
              body: { flights: flightInputs },
            });

            newResults.push({
              function: "enrich-flight-price",
              success: !error && data?.enrichment,
              message: error?.message || (data?.enrichment
                ? `Retail price: ${data.enrichment.currency} ${data.enrichment.retail_price}`
                : data?.message || "No price data returned"),
              data: data?.enrichment,
            });
          } catch (err) {
            newResults.push({
              function: "enrich-flight-price",
              success: false,
              message: err instanceof Error ? err.message : "Exception occurred",
            });
          }
        } else {
          newResults.push({
            function: "enrich-flight-price",
            success: false,
            message: "No flights in this trip",
          });
        }
      }

      // Run hotel price enrichment
      if (selectedFunctions.has("enrich-hotel-price")) {
        for (let i = 0; i < hotels.length; i++) {
          const hotel = hotels[i];
          try {
            const { data, error } = await supabase.functions.invoke("enrich-hotel-price", {
              body: {
                hotel: {
                  hotel_name: hotel.hotel_name,
                  city: hotel.city,
                  check_in_date: hotel.check_in_date,
                  check_out_date: hotel.check_out_date,
                  room_type: hotel.room_type,
                },
              },
            });

            newResults.push({
              function: `enrich-hotel-price (${hotel.hotel_name})`,
              success: !error && data?.enrichment,
              message: error?.message || (data?.enrichment
                ? `Retail price: ${data.enrichment.currency} ${data.enrichment.retail_price}`
                : data?.message || "No price data returned"),
              data: data?.enrichment,
            });
          } catch (err) {
            newResults.push({
              function: `enrich-hotel-price (${hotel.hotel_name})`,
              success: false,
              message: err instanceof Error ? err.message : "Exception occurred",
            });
          }
        }
        if (hotels.length === 0) {
          newResults.push({
            function: "enrich-hotel-price",
            success: false,
            message: "No hotels in this trip",
          });
        }
      }

      // Run cruise enrichment
      if (selectedFunctions.has("enrich-cruise")) {
        for (let i = 0; i < cruises.length; i++) {
          const cruise = cruises[i];
          try {
            const { data, error } = await supabase.functions.invoke("enrich-cruise", {
              body: {
                ship_name: cruise.ship_name,
                cruise_line: cruise.cruise_line,
                departure_date: cruise.departure_date,
                arrival_date: cruise.arrival_date,
              },
            });

            newResults.push({
              function: `enrich-cruise (${cruise.ship_name})`,
              success: !error && data?.enrichment,
              message: error?.message || (data?.enrichment
                ? `Found ${data.enrichment.itinerary_stops?.length || 0} stops`
                : data?.message || "No itinerary data returned"),
              data: data?.enrichment,
            });
          } catch (err) {
            newResults.push({
              function: `enrich-cruise (${cruise.ship_name})`,
              success: false,
              message: err instanceof Error ? err.message : "Exception occurred",
            });
          }
        }
        if (cruises.length === 0) {
          newResults.push({
            function: "enrich-cruise",
            success: false,
            message: "No cruises in this trip",
          });
        }
      }

      setResults(newResults);

      // Show summary toast
      const successCount = newResults.filter((r) => r.success).length;
      const failCount = newResults.filter((r) => !r.success).length;

      if (successCount > 0 && failCount === 0) {
        toast.success(`All ${successCount} enrichment(s) succeeded`);
      } else if (successCount > 0) {
        toast.info(`${successCount} succeeded, ${failCount} failed`);
      } else {
        toast.error(`All ${failCount} enrichment(s) failed`);
      }

      // Invalidate trip queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["trip", selectedTripId] });
      queryClient.invalidateQueries({ queryKey: ["trips"] });

    } catch (err) {
      toast.error("Error running enrichments: " + (err instanceof Error ? err.message : "Unknown error"));
    } finally {
      setIsRunning(false);
    }
  };

  // Get item counts for selected trip
  const flightCount = selectedTrip?.trip_data?.flights?.length || 0;
  const hotelCount = selectedTrip?.trip_data?.hotels?.length || 0;
  const cruiseCount = selectedTrip?.trip_data?.cruises?.length || 0;

  return (
    <div className="mt-10 rounded-xl border border-border bg-card shadow-soft overflow-hidden">
      <div className="border-b border-border px-6 py-4">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-accent" />
          <h2 className="font-display text-lg font-semibold text-card-foreground">
            Enrichment Debug
          </h2>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Re-run edge functions and API calls for a specific trip to debug enrichment issues.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Trip Selector */}
        <div className="space-y-2">
          <Label>Select Trip</Label>
          <Select value={selectedTripId} onValueChange={setSelectedTripId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Choose a trip..." />
            </SelectTrigger>
            <SelectContent>
              {trips?.map((trip) => (
                <SelectItem key={trip.id} value={trip.id}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{trip.display_name}</span>
                    {trip.start_date && (
                      <span className="text-xs text-muted-foreground">
                        ({safeFormat(trip.start_date, "MMM d, yyyy")})
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedTrip && (
            <div className="flex gap-4 text-xs text-muted-foreground mt-2">
              <span className="flex items-center gap-1">
                <Plane className="h-3 w-3" /> {flightCount} flight(s)
              </span>
              <span className="flex items-center gap-1">
                <Hotel className="h-3 w-3" /> {hotelCount} hotel(s)
              </span>
              <span className="flex items-center gap-1">
                <Ship className="h-3 w-3" /> {cruiseCount} cruise(s)
              </span>
            </div>
          )}
        </div>

        {/* Function Selector */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Select Functions</Label>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={selectAll} className="h-7 text-xs">
                Select All
              </Button>
              <Button variant="ghost" size="sm" onClick={selectNone} className="h-7 text-xs">
                Clear
              </Button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {ENRICHMENT_FUNCTIONS.map((fn) => {
              const Icon = fn.icon;
              const isSelected = selectedFunctions.has(fn.id);

              return (
                <div
                  key={fn.id}
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    isSelected ? "border-accent bg-accent/5" : "border-border hover:border-accent/50"
                  }`}
                  onClick={() => toggleFunction(fn.id)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleFunction(fn.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">{fn.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{fn.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Run Button */}
        <Button
          onClick={runEnrichment}
          disabled={!selectedTripId || selectedFunctions.size === 0 || isRunning}
          className="w-full"
        >
          {isRunning ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Running Enrichments...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Run Selected Enrichments
            </>
          )}
        </Button>

        {/* Results */}
        {results.length > 0 && (
          <div className="space-y-2">
            <Label>Results</Label>
            <div className="rounded-lg border border-border divide-y divide-border max-h-[300px] overflow-auto">
              {results.map((result, idx) => (
                <div
                  key={idx}
                  className={`p-3 text-sm ${
                    result.success ? "bg-emerald-500/5" : "bg-amber-500/5"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {result.success ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{result.function}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {result.message}
                      </div>
                      {result.data && (
                        <details className="mt-2">
                          <summary className="text-xs text-accent cursor-pointer hover:underline">
                            View raw response
                          </summary>
                          <pre className="mt-2 p-2 rounded bg-muted text-xs overflow-auto max-h-[200px]">
                            {JSON.stringify(result.data, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
